import {
  createMatterLitigationDatePlanDateV1,
  deleteMatterLitigationDatePlanDateV1,
  retrieveMatterLitigationDatePlanDatesV1,
  updateMatterLitigationDatePlanDateV1,
} from "@/lib/data/requests/matters/matter-litigation-date-plan-date";
import { queryKeys } from "@/lib/data/static/query-keys";
import {
  type APIError,
  type APIResponse,
  type MatterLitigationDatePlanDate,
} from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import toast from "react-hot-toast";

export const retrieveMatterLitigationDatePlanDatesQueryOptions = (id: string) =>
  queryOptions({
    queryKey: [queryKeys.matterLitigationDatePlanDates, id],
    queryFn: async () => {
      return retrieveMatterLitigationDatePlanDatesV1(id);
    },
    enabled: !!id,
  });

export const useCreateMatterLitigationDatePlanDate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      data: Parameters<typeof createMatterLitigationDatePlanDateV1>[0],
    ) => createMatterLitigationDatePlanDateV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error creating plan date");
    },
    onSettled: async (_, __, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matterLitigationDatePlanDates, variables.planId],
      });
    },
    onSuccess: () => {
      toast.success("Plan date created successfully");
    },
  });
};

export const useUpdateMatterLitigationDatePlanDate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      data: Parameters<typeof updateMatterLitigationDatePlanDateV1>[0],
    ) => updateMatterLitigationDatePlanDateV1(data),
    onMutate: (data) => {
      queryClient.setQueryData(
        [queryKeys.matterLitigationDatePlanDates, data.planId],
        (oldData: APIResponse<MatterLitigationDatePlanDate[]> | undefined) => {
          if (!oldData) return;

          const existingDates = Array.isArray(oldData.data) ? oldData.data : [];

          return {
            ...oldData,
            data: existingDates.map((item) =>
              item.id === data.id ? { ...item, ...data } : item,
            ),
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([
          queryKeys.matterLitigationDatePlanDates,
          data.planId,
        ]),
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.matterLitigationDatePlanDates, variables.planId],
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error updating plan date");
    },
    onSettled: async (_, __, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matterLitigationDatePlanDates, variables.planId],
      });
    },
    onSuccess: () => {
      toast.success("Plan date updated successfully");
    },
  });
};

export const useDeleteMatterLitigationDatePlanDate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      data: Parameters<typeof deleteMatterLitigationDatePlanDateV1>[0],
    ) => deleteMatterLitigationDatePlanDateV1(data),
    onMutate: (data) => {
      queryClient.setQueryData(
        [queryKeys.matterLitigationDatePlanDates, data.planId],
        (oldData: APIResponse<MatterLitigationDatePlanDate[]> | undefined) => {
          if (!oldData) return;

          const existingDates = Array.isArray(oldData.data) ? oldData.data : [];

          return {
            ...oldData,
            data: existingDates.filter((item) => item.id !== data.id),
          };
        },
      );
      return {
        previousData: queryClient.getQueryData([
          queryKeys.matterLitigationDatePlanDates,
          data.planId,
        ]),
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.matterLitigationDatePlanDates, variables.planId],
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error deleting plan date");
    },
    onSettled: async (_, __, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matterLitigationDatePlanDates, variables.planId],
      });
    },
    onSuccess: () => {
      toast.success("Plan date deleted successfully");
    },
  });
};
